import type { RequiredDocumentTypeInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import {
  createRequiredDocumentType,
  deactivateRequiredDocumentType,
  listRequiredDocumentTypes,
  reactivateRequiredDocumentType,
  updateRequiredDocumentType,
  type RequiredDocumentType,
} from './reference-data.api';

export type RequiredDocumentTypesState =
  { status: 'loading' } | { status: 'loaded'; types: RequiredDocumentType[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): RequiredDocumentTypesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

function toSaveError(err: unknown): string {
  return err instanceof ApiError && err.status === 409
    ? 'A required document type with this code already exists.'
    : 'Could not save — check the fields and try again.';
}

/** Same shape as useTrailerTypes: create/update keep their error inline, deactivate/reactivate toast. */
export function useRequiredDocumentTypes() {
  const [state, setState] = useState<RequiredDocumentTypesState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listRequiredDocumentTypes()
      .then((types) => {
        if (!cancelled) setState({ status: 'loaded', types });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', types: await listRequiredDocumentTypes() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(toSaveError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  const create = (input: RequiredDocumentTypeInput) => save(() => createRequiredDocumentType(input), `${input.name} created`);
  const update = (id: string, input: RequiredDocumentTypeInput) => save(() => updateRequiredDocumentType(id, input), `${input.name} updated`);
  const deactivate = (row: RequiredDocumentType) =>
    rowAction(() => deactivateRequiredDocumentType(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: RequiredDocumentType) =>
    rowAction(() => reactivateRequiredDocumentType(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, saveError, saving, create, update, resetSaveError: () => setSaveError(null), deactivate, reactivate };
}
